/**
 * Omnisearch and subsidiary list API schemas.
 */

import { z } from 'zod';
import type {
    CursorSearchRequest,
    ListDirectOrganizationUsersPageQuery,
    OmnisearchSearchRequest,
    OrganizationOmnisearchSearchRequest,
    OpaqueCursorSearchRequest,
    UserOmnisearchSearchRequest,
} from '../omnisearch.types';
import {
    OrganizationOmnisearchListItemSchema,
} from './organization.schemas';
import {
    UserProfileDetailedPublicResponseSchema,
} from './user.schemas';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import {
    AuthorizationOpaqueCursorQuerySchema,
    AuthorizationPaginationCursorQuerySchema,
    AuthorizationPaginationLimitQuerySchema,
} from './authorization.schemas';
import {
    PhysicalJsonStructurePredicateOperatorSchema,
    PhysicalJsonStructurePredicateSchema,
    PhysicalJsonStructurePredicatesSchema,
    SearchFieldPredicateSchema,
    SearchFieldPredicatesSchema,
    SearchJsonPathPredicateSchema,
    SearchJsonPathPredicatesSchema,
    SearchTermsSchema,
} from './common.schemas';

export {
    PhysicalJsonStructurePredicateOperatorSchema,
    PhysicalJsonStructurePredicateSchema,
    PhysicalJsonStructurePredicatesSchema,
    SearchFieldPredicateSchema,
    SearchFieldPredicatesSchema,
    SearchJsonPathPredicateSchema,
    SearchJsonPathPredicatesSchema,
    SearchTermsSchema,
};

export const CursorPageQuerySchema = z.object({
    limit: AuthorizationPaginationLimitQuerySchema,
    cursor: AuthorizationPaginationCursorQuerySchema,
});

export type CursorPageQuery = z.infer<typeof CursorPageQuerySchema>;

export const OpaqueCursorPageQuerySchema = z.object({
    limit: AuthorizationPaginationLimitQuerySchema,
    cursor: AuthorizationOpaqueCursorQuerySchema,
});

export type OpaqueCursorPageQuery = z.infer<typeof OpaqueCursorPageQuerySchema>;

const IncludeDescendantsQuerySchemaBase: z.ZodType<boolean | undefined> = z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((value: boolean | 'true' | 'false'): boolean => value === true || value === 'true')
    .optional();
export const IncludeDescendantsQuerySchema: z.ZodType<boolean | undefined> =
    IncludeDescendantsQuerySchemaBase;

export const ListDirectOrganizationUsersPageQuerySchema: z.ZodType<
    ListDirectOrganizationUsersPageQuery
> = z.object({
    limit: AuthorizationPaginationLimitQuerySchema,
    cursor: AuthorizationOpaqueCursorQuerySchema,
    include_descendants: IncludeDescendantsQuerySchema,
});

export const SearchScopeOrganizationUuidSchema: z.ZodType<string | undefined> = z
    .string()
    .uuid()
    .optional();

export const CursorSearchRequestSchema: z.ZodType<CursorSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: AuthorizationPaginationCursorQuerySchema,
        field_predicates: SearchFieldPredicatesSchema,
        json_path_predicates: PhysicalJsonStructurePredicatesSchema,
    })
    .strict();

export const OpaqueCursorSearchRequestSchema: z.ZodType<OpaqueCursorSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: AuthorizationOpaqueCursorQuerySchema,
        field_predicates: SearchFieldPredicatesSchema,
        json_path_predicates: PhysicalJsonStructurePredicatesSchema,
    })
    .strict();

export const OmnisearchSearchRequestSchema: z.ZodType<OmnisearchSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: AuthorizationPaginationCursorQuerySchema,
        field_predicates: SearchFieldPredicatesSchema,
        json_path_predicates: PhysicalJsonStructurePredicatesSchema,
    })
    .strict();

export const UserOmnisearchQuerySchema = z.object({
    q: z.string().trim().optional(),
    limit: AuthorizationPaginationLimitQuerySchema,
    cursor: AuthorizationOpaqueCursorQuerySchema,
    organization_uuid: SearchScopeOrganizationUuidSchema,
});

export type UserOmnisearchQuery = z.infer<typeof UserOmnisearchQuerySchema>;

export const UserOmnisearchSearchRequestSchema: z.ZodType<UserOmnisearchSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: AuthorizationOpaqueCursorQuerySchema,
        organization_uuid: SearchScopeOrganizationUuidSchema,
    })
    .strict();

const OrganizationCapacityFilterQueryValueSchema: z.ZodType<true | undefined> = z
    .union([z.literal(true), z.enum(['true'])])
    .optional()
    .transform((value: true | 'true' | undefined): true | undefined => {
        if (value === undefined) {
            return undefined;
        }
        return true;
    });

export const OrganizationCapacityFilterQuerySchema: z.ZodObject<{
    is_iso: z.ZodType<true | undefined>;
    is_merchant: z.ZodType<true | undefined>;
    is_customer: z.ZodType<true | undefined>;
}> = z.object({
    is_iso: OrganizationCapacityFilterQueryValueSchema,
    is_merchant: OrganizationCapacityFilterQueryValueSchema,
    is_customer: OrganizationCapacityFilterQueryValueSchema,
});

export const OrganizationOmnisearchSearchRequestSchema: z.ZodType<
    OrganizationOmnisearchSearchRequest
> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: AuthorizationOpaqueCursorQuerySchema,
        is_iso: OrganizationCapacityFilterQueryValueSchema,
        is_merchant: OrganizationCapacityFilterQueryValueSchema,
        is_customer: OrganizationCapacityFilterQueryValueSchema,
        organization_uuid: SearchScopeOrganizationUuidSchema,
        include_descendants: z.boolean().optional(),
        show_deleted: z.coerce.boolean().optional(),
    })
    .strict();

export const OrganizationOmnisearchPageResponseSchema = z.object({
    organizations: z.array(OrganizationOmnisearchListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});

export const UserOmnisearchPageResponseSchema = z.object({
    users: z.array(UserProfileDetailedPublicResponseSchema),
    pagination: OpaqueCursorPaginationSchema,
});

export const ListSubsidiaryIsosPageResponseSchema = z.object({
    isos: z.array(OrganizationOmnisearchListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});

export const ListSubsidiaryMerchantsPageResponseSchema = z.object({
    merchants: z.array(OrganizationOmnisearchListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});

export const ListDirectOrganizationUsersPageResponseSchema = z.object({
    users: z.array(UserProfileDetailedPublicResponseSchema),
    pagination: OpaqueCursorPaginationSchema,
});
